import React, { useState } from 'react';
import { loadNotificationPrefs, saveNotificationPrefs, persistPrefsToBackend } from '../services/notifications';

export default function NotificationSettings({ user, darkMode }) {
  const defaultPrefs = loadNotificationPrefs();
  const [prefs, setPrefs] = useState({
    ...defaultPrefs,
    coachingRemindersFrequency: defaultPrefs.coachingRemindersFrequency || 'weekly',
    coachingRemindersInterval: defaultPrefs.coachingRemindersInterval || 1, // For weekly/biweekly
    coachingRemindersMonthlyDay: defaultPrefs.coachingRemindersMonthlyDay || 1, // Day of month (1-31)
    coachingRemindersMonthlyWeek: defaultPrefs.coachingRemindersMonthlyWeek || 'first', // first, second, third, fourth, last
    coachingRemindersMonthlyWeekday: defaultPrefs.coachingRemindersMonthlyWeekday || 'Mon', // Day of week for monthly
  });
  const [saving, setSaving] = useState(false);

  const theme = {
    card: darkMode ? 'rgba(49, 46, 129, 0.4)' : 'rgba(255, 255, 255, 0.85)',
    border: darkMode ? 'rgba(224, 231, 255, 0.15)' : 'rgba(30, 41, 59, 0.1)',
    text: darkMode ? '#ffffff' : '#1e293b',
    subtext: darkMode ? '#e0e7ff' : '#64748b',
    accent: darkMode ? '#818cf8' : '#8b5cf6',
    input: darkMode ? 'rgba(30, 41, 59, 0.5)' : '#ffffff',
    button: darkMode ? '#818cf8' : '#8b5cf6',
  };

  // Time options (every 15 minutes)
  const timeOptions = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 15) {
      const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
      const ampm = hour < 12 ? 'AM' : 'PM';
      const minuteStr = String(minute).padStart(2, '0');
      const time24 = `${String(hour).padStart(2, '0')}:${minuteStr}`;
      const time12 = `${hour12}:${minuteStr} ${ampm}`;
      timeOptions.push({ value: time24, label: time12 });
    }
  }

  // Frequency/Interval options
  const frequencyOptions = [
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'biweekly', label: 'Bi-weekly (Every 2 weeks)' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'custom', label: 'Custom (Specific days)' }
  ];

  // Day options
  const dayOptions = [
    { value: 'Mon', label: 'Monday' },
    { value: 'Tue', label: 'Tuesday' },
    { value: 'Wed', label: 'Wednesday' },
    { value: 'Thu', label: 'Thursday' },
    { value: 'Fri', label: 'Friday' },
    { value: 'Sat', label: 'Saturday' },
    { value: 'Sun', label: 'Sunday' },
  ];

  // Preset day combinations
  const dayPresets = [
    { label: 'Weekdays', value: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'] },
    { label: 'Weekends', value: ['Sat', 'Sun'] },
    { label: 'Daily', value: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] },
    { label: 'Monday, Wednesday, Friday', value: ['Mon', 'Wed', 'Fri'] },
    { label: 'Tuesday, Thursday', value: ['Tue', 'Thu'] },
  ];

  const handleTimeChange = (e) => {
    setPrefs({ ...prefs, coachingRemindersTime: e.target.value });
  };

  const handleDayToggle = (day) => {
    const currentDays = prefs.coachingRemindersDays || [];
    const newDays = currentDays.includes(day)
      ? currentDays.filter(d => d !== day)
      : [...currentDays, day];
    setPrefs({ ...prefs, coachingRemindersDays: newDays });
  };

  const handlePresetSelect = (presetDays) => {
    setPrefs({ ...prefs, coachingRemindersDays: presetDays });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const saved = saveNotificationPrefs(prefs);
      if (user?.uid || user?.email) {
        await persistPrefsToBackend(saved, {
          userId: user.uid || null,
          email: user.email || null,
        });
      }
      alert('Notification preferences saved!');
    } catch (error) {
      console.error('Error saving preferences:', error);
      alert('Failed to save preferences. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{
      background: theme.card,
      border: `1px solid ${theme.border}`,
      borderRadius: '16px',
      padding: '24px',
      color: theme.text
    }}>
      <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '8px' }}>
        Notifications
      </h3>
      <p style={{ fontSize: '14px', color: theme.subtext, marginBottom: '24px' }}>
        Configure when you'd like to receive coaching reminders.
      </p>

      {/* Coaching Reminders Toggle */}
      <div style={{ marginBottom: '24px' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
          <input
            type="checkbox"
            checked={prefs.coachingReminders || false}
            onChange={(e) => setPrefs({ ...prefs, coachingReminders: e.target.checked })}
            style={{ width: '18px', height: '18px', cursor: 'pointer' }}
          />
          <span style={{ fontWeight: '500', fontSize: '16px' }}>Enable Coaching Reminders</span>
        </label>
      </div>

      {prefs.coachingReminders && (
        <>
          {/* Frequency/Interval Selection */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '14px' }}>
              Frequency
            </label>
            <select
              value={prefs.coachingRemindersFrequency || 'weekly'}
              onChange={(e) => setPrefs({ ...prefs, coachingRemindersFrequency: e.target.value })}
              style={{
                width: '100%',
                maxWidth: '300px',
                padding: '10px 12px',
                background: theme.input,
                border: `1px solid ${theme.border}`,
                borderRadius: '8px',
                color: theme.text,
                fontSize: '14px',
                cursor: 'pointer'
              }}
            >
              {frequencyOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Weekly Interval (for weekly/biweekly) */}
          {(prefs.coachingRemindersFrequency === 'weekly' || prefs.coachingRemindersFrequency === 'biweekly') && (
            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '14px' }}>
                {prefs.coachingRemindersFrequency === 'biweekly' ? 'Every 2 weeks on:' : 'Day of Week:'}
              </label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {dayOptions.map(day => (
                  <label
                    key={day.value}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      cursor: 'pointer',
                      padding: '8px 12px',
                      border: `1px solid ${prefs.coachingRemindersDays?.includes(day.value) ? theme.accent : theme.border}`,
                      borderRadius: '8px',
                      background: prefs.coachingRemindersDays?.includes(day.value) 
                        ? (darkMode ? 'rgba(129, 140, 248, 0.2)' : 'rgba(139, 92, 246, 0.1)')
                        : 'transparent',
                      transition: 'all 0.2s'
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={prefs.coachingRemindersDays?.includes(day.value) || false}
                      onChange={() => handleDayToggle(day.value)}
                      style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                    />
                    <span style={{ fontSize: '14px', userSelect: 'none' }}>{day.label}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Monthly Options */}
          {prefs.coachingRemindersFrequency === 'monthly' && (
            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', marginBottom: '12px', fontWeight: '500', fontSize: '14px' }}>
                Monthly Schedule
              </label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', color: theme.subtext }}>
                    Schedule Type
                  </label>
                  <select
                    value={prefs.coachingRemindersMonthlyType || 'day'}
                    onChange={(e) => setPrefs({ ...prefs, coachingRemindersMonthlyType: e.target.value })}
                    style={{
                      width: '100%',
                      maxWidth: '300px',
                      padding: '10px 12px',
                      background: theme.input,
                      border: `1px solid ${theme.border}`,
                      borderRadius: '8px',
                      color: theme.text,
                      fontSize: '14px',
                      cursor: 'pointer'
                    }}
                  >
                    <option value="day">Specific Day of Month (e.g., 1st, 15th)</option>
                    <option value="weekday">Day of Week in Month (e.g., First Monday, Last Friday)</option>
                  </select>
                </div>
                
                {prefs.coachingRemindersMonthlyType === 'day' ? (
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', color: theme.subtext }}>
                      Day of Month (1-31)
                    </label>
                    <select
                      value={prefs.coachingRemindersMonthlyDay || 1}
                      onChange={(e) => setPrefs({ ...prefs, coachingRemindersMonthlyDay: parseInt(e.target.value) })}
                      style={{
                        width: '100%',
                        maxWidth: '200px',
                        padding: '10px 12px',
                        background: theme.input,
                        border: `1px solid ${theme.border}`,
                        borderRadius: '8px',
                        color: theme.text,
                        fontSize: '14px',
                        cursor: 'pointer'
                      }}
                    >
                      {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
                        <option key={day} value={day}>
                          {day}{day === 1 ? 'st' : day === 2 ? 'nd' : day === 3 ? 'rd' : 'th'}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', color: theme.subtext }}>
                        Week of Month
                      </label>
                      <select
                        value={prefs.coachingRemindersMonthlyWeek || 'first'}
                        onChange={(e) => setPrefs({ ...prefs, coachingRemindersMonthlyWeek: e.target.value })}
                        style={{
                          padding: '10px 12px',
                          background: theme.input,
                          border: `1px solid ${theme.border}`,
                          borderRadius: '8px',
                          color: theme.text,
                          fontSize: '14px',
                          cursor: 'pointer'
                        }}
                      >
                        <option value="first">First</option>
                        <option value="second">Second</option>
                        <option value="third">Third</option>
                        <option value="fourth">Fourth</option>
                        <option value="last">Last</option>
                      </select>
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', color: theme.subtext }}>
                        Day of Week
                      </label>
                      <select
                        value={prefs.coachingRemindersMonthlyWeekday || 'Mon'}
                        onChange={(e) => setPrefs({ ...prefs, coachingRemindersMonthlyWeekday: e.target.value })}
                        style={{
                          padding: '10px 12px',
                          background: theme.input,
                          border: `1px solid ${theme.border}`,
                          borderRadius: '8px',
                          color: theme.text,
                          fontSize: '14px',
                          cursor: 'pointer'
                        }}
                      >
                        {dayOptions.map(day => (
                          <option key={day.value} value={day.value}>
                            {day.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Daily - no additional options needed */}
          {prefs.coachingRemindersFrequency === 'daily' && (
            <div style={{ marginBottom: '24px', padding: '12px', background: darkMode ? 'rgba(129, 140, 248, 0.1)' : 'rgba(139, 92, 246, 0.05)', borderRadius: '8px' }}>
              <p style={{ fontSize: '13px', color: theme.subtext, margin: 0 }}>
                Reminders will be sent every day at the specified time.
              </p>
            </div>
          )}

          {/* Custom - show day selection */}
          {prefs.coachingRemindersFrequency === 'custom' && (
            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', marginBottom: '12px', fontWeight: '500', fontSize: '14px' }}>
                Select Days
              </label>
              
              {/* Preset Options */}
              <div style={{ marginBottom: '16px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {dayPresets.map((preset, idx) => (
                  <button
                    key={idx}
                    onClick={() => handlePresetSelect(preset.value)}
                    style={{
                      padding: '6px 12px',
                      background: theme.button,
                      border: 'none',
                      borderRadius: '6px',
                      color: 'white',
                      fontSize: '13px',
                      cursor: 'pointer',
                      opacity: 0.8
                    }}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>

              {/* Individual Day Checkboxes */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                {dayOptions.map(day => (
                  <label
                    key={day.value}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      cursor: 'pointer',
                      padding: '8px 12px',
                      border: `1px solid ${prefs.coachingRemindersDays?.includes(day.value) ? theme.accent : theme.border}`,
                      borderRadius: '8px',
                      background: prefs.coachingRemindersDays?.includes(day.value) 
                        ? (darkMode ? 'rgba(129, 140, 248, 0.2)' : 'rgba(139, 92, 246, 0.1)')
                        : 'transparent',
                      transition: 'all 0.2s'
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={prefs.coachingRemindersDays?.includes(day.value) || false}
                      onChange={() => handleDayToggle(day.value)}
                      style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                    />
                    <span style={{ fontSize: '14px', userSelect: 'none' }}>{day.label}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Time Selection */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '14px' }}>
              Time
            </label>
            <select
              value={prefs.coachingRemindersTime || '09:00'}
              onChange={handleTimeChange}
              style={{
                width: '100%',
                maxWidth: '300px',
                padding: '10px 12px',
                background: theme.input,
                border: `1px solid ${theme.border}`,
                borderRadius: '8px',
                color: theme.text,
                fontSize: '14px',
                cursor: 'pointer'
              }}
            >
              {timeOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </>
      )}

      {/* Save Button */}
      <button
        onClick={handleSave}
        disabled={saving}
        style={{
          padding: '12px 24px',
          background: theme.button,
          border: 'none',
          borderRadius: '8px',
          color: 'white',
          fontSize: '14px',
          fontWeight: '600',
          cursor: saving ? 'not-allowed' : 'pointer',
          opacity: saving ? 0.6 : 1,
          width: '100%',
          maxWidth: '200px'
        }}
      >
        {saving ? 'Saving...' : 'Save'}
      </button>
    </div>
  );
}

